import { canUseWindow, getClientProtocol } from './browserEnvironment'
import {
  getClientEnvironment,
  getClientLocalDev,
  getClientDomain,
  getClientAssetsDomain,
  getClientCheckoutComPublicKey,
  getClientRecaptchaPublicKey,
  getClientApplePayMerchantId,
  getClientGooglePayMerchantId,
} from './configFromWindow'
import {
  getServerEnvironment,
  getServerLocalDev,
  getServerDomain,
  getServerAssetsDomain,
  getServerProtocol,
  getServerCheckoutComPublicKey,
  getServerRecaptchaPublicKey,
  getServerApplePayMerchantId,
  getServerGooglePayMerchantId,
} from './serverEnvironment'

/**
 * *************************************************************************************
 * * ⚠️ This file is being migrated from webclient. Sync with:                         *
 * * ↔️️ webclient/src/utils/isomorphicEnvironment.ts                                   *
 * *************************************************************************************
 */

type CreateIsomorphicConfig<T> = {
  testFn?: () => boolean
  browserConfigFn: () => T
  serverConfigFn: () => T
}

/**
 * Utility to create an isomorphic environment
 * config getter
 *
 * Can override environment test fn if needed,
 * defaults to `canUseWindow`
 *
 * @example
 * ```
 * const getDomain = createIsomorphicConfig({
 *  browserConfigFn: getClientDomain,
 *  serverConfigFn: getServerDomain,
 * })
 *
 * // In use:
 * const domain = getDomain()
 * ```
 */
export const createIsomorphicConfig = <T>({
  testFn,
  browserConfigFn,
  serverConfigFn,
}: CreateIsomorphicConfig<T>) => {
  const shouldUseBrowserFn = testFn || canUseWindow

  return () => (shouldUseBrowserFn() ? browserConfigFn() : serverConfigFn())
}

export const getEnvironment = createIsomorphicConfig({
  browserConfigFn: getClientEnvironment,
  serverConfigFn: getServerEnvironment,
})

const getLocalDevFlag = createIsomorphicConfig({
  browserConfigFn: getClientLocalDev,
  serverConfigFn: getServerLocalDev,
})

/**
 * Whether we are running on a local developer machine.
 *
 * Intended to be driven solely by the LOCAL_DEV flag, so the deployed environment
 * identity (e.g. 'staging') can be used locally without inheriting local-only
 * behaviours (http protocol, non-secure cookies, console logging/telemetry,
 * SSR using public API URLs, staging-api backend redirects).
 *
 * TRANSITIONAL: also returns true for the legacy `ENVIRONMENT === 'local'` so that
 * consumers can be migrated onto `isLocalDev` without changing behaviour while every
 * environment still sets `ENVIRONMENT=local`. The `ENVIRONMENT === 'local'` fallback
 * is removed once environments adopt `LOCAL_DEV=true`, freeing up the `local` value.
 */
export const isLocalDev = () => getLocalDevFlag() || getEnvironment() === 'local'

// TODO - narrow type for both fns here
export const getDomain = createIsomorphicConfig({
  browserConfigFn: getClientDomain,
  serverConfigFn: getServerDomain,
})
export const getAssetsDomain = createIsomorphicConfig({
  browserConfigFn: getClientAssetsDomain,
  serverConfigFn: getServerAssetsDomain,
})

export const getProtocol = createIsomorphicConfig({
  browserConfigFn: getClientProtocol,
  serverConfigFn: getServerProtocol,
})

export const isProd = () => getEnvironment() === 'production'
/**
 * Whether we are running on a local developer machine. Kept as the established
 * name used across the codebase; delegates to `isLocalDev` (the LOCAL_DEV flag
 * with a transitional ENVIRONMENT === 'local' fallback). Defined as a wrapper
 * rather than a bare alias so it is always a function under partial module mocks.
 */
export const isDev = () => isLocalDev()

export const getRecaptchaPublicKey = createIsomorphicConfig({
  browserConfigFn: getClientRecaptchaPublicKey,
  serverConfigFn: getServerRecaptchaPublicKey,
})

export const getCheckoutComPublicKey = createIsomorphicConfig({
  browserConfigFn: getClientCheckoutComPublicKey,
  serverConfigFn: getServerCheckoutComPublicKey,
})

export const getApplePayMerchantId = createIsomorphicConfig({
  browserConfigFn: getClientApplePayMerchantId,
  serverConfigFn: getServerApplePayMerchantId,
})

export const getGooglePayMerchantId = createIsomorphicConfig({
  browserConfigFn: getClientGooglePayMerchantId,
  serverConfigFn: getServerGooglePayMerchantId,
})
